import { connect } from 'react-redux';
import { FontIcon } from '@fluentui/react';
import { useEffect, useState } from 'react';
import MOMENT from 'react-moment';
import { Button, Spinner, Toaster, ToastPosition, Tooltip, useToastController } from '@fluentui/react-components';
import { ArrowUpload20Regular } from '@fluentui/react-icons';
import { AppToast } from '../../../commons/component/AppToast/AppToast.Component';
import { List, ListItem } from "@fluentui/react-migration-v0-v9";
import { Separator } from '@fluentui/react';
import { RiPrinterLine } from 'react-icons/ri';

//functions
import * as ComponentFunctions from './SettlementHeader.Functions';
import { resolveSettlementKind, settlementKindMeta } from '../../../functions/SettlementKind.Functions';

////i18Next
import { useTranslation } from 'react-i18next';

// components
import SettlementChangeStatusConfirmationFormComponent from '../SettlementChangeStatusConfirmationForm/SettlementChangeStatusConfirmation.Form.Component';

// services
import { GetSettlementPdf } from '../../../services/Settlements.Services';

// Styles
import './SettlementHeader.Styles.css';
import { SettlementStatusEnum } from '../../../models/Enums/SettlementStatusEnum';
import SettlementDebtsDetailsComponent from '../SettllementDebtsDetails/SettlementDebtsDetails.Component';
import SettlementNameFormComponent from '../SettlementNameForm/SettlementNameForm.Component';

export const SettlementHeaderComponent = (props: ComponentFunctions.Props) => {

    const { t } = useTranslation();
    const { dispatchToast } = useToastController('SettlementToast');
    const [position] = useState<ToastPosition>('bottom-end');
    const [timeout] = useState<number>(5000);
    const [debtsCollapsed, setDebtsCollapsed] = useState<boolean>(true);
    const [isRenaming, setIsRenaming] = useState<boolean>(false);
    const [isPrinting, setIsPrinting] = useState<boolean>(false);

    useEffect(() => {
        props.GetSettlementById(props.SettlementId);
    }, []);

    useEffect(() => {
        if (props.failOnChangeStatus === true) {
            dispatchToast(
                <AppToast intent="error" title="Error al confirmar la liquidaci\u00f3n" body={props.error?.ErrorMessage} items={props.error?.Errors} />,
                { timeout, position, intent: 'error' }
            );
        }
    }, [props.failOnChangeStatus]);

    useEffect(() => {
        if (props.changeStatusSuccessfully) {
            props.GetSettlementById(props.SettlementId);
            props.ResetSettlementChangeStatusFlags(props.SettlementId);
        }
    }, [props.changeStatusSuccessfully]);

    // Fetch associated entity if not yet loaded in the store
    useEffect(() => {
        const s = props.Settlement;
        if (!s) return;
        // El proveedor trae su razón social en la asociación: no se pide nada.
        if (resolveSettlementKind(s) === 'provider') return;
        if ((s.sponsor?.length ?? 0) > 0 && !props.settlementEntityLabel) {
            props.GetSponsorById(s.sponsor[0].sponsorId);
        } else if ((s.practitioner?.length ?? 0) > 0 && (s.protocol?.length ?? 0) > 0 && !props.settlementEntityLabel) {
            const practitionerId = ComponentFunctions.settlementPractitionerId(s);
            if (practitionerId) props.GetPractitionerById(practitionerId);
        } else if ((s.agreements?.length ?? 0) > 0 && !props.settlementEntityLabel) {
            props.GetMedicalInsuranceById(s.medicalInsuranceId);
        }
    }, [props.Settlement?.id]);

    useEffect(() => {
        if (props.Settlement) {
            props.Settlement?.protocol?.[0]?.protocolId && props.GetMedicalProtocolById(props.Settlement?.protocol?.[0]?.protocolId);
            props.Settlement?.contract?.[0]?.contractId && props.GetContractById(props.Settlement?.contract?.[0]?.contractId);
        }
    }, [props.Settlement?.protocol?.[0]?.protocolId, props.Settlement?.contract?.[0]?.contractId]);

    const statusMod = (() => {
        switch (props.Settlement?.status) {
            case 3:  return 'finished';
            case 4:  return 'cancelled';
            case 5:  return 'confirmed';
            case 6:  return 'rejected';
            case 7:  return 'fail';
            case 8:  return 'paid';
            case 9:  return 'debt';
            default: return 'default';
        }
    })();

    // Tipo: settlementType 4 primero, asociaciones de respaldo (spec 010, resolveSettlementKind).
    const settlementKind = resolveSettlementKind(props.Settlement);
    const settlementType = (() => {
        const meta = settlementKindMeta(settlementKind);
        return meta ? { label: t(meta.labelKey), icon: meta.icon, mod: settlementKind } : null;
    })();
    const entityLabelKey = props.settlementEntityType
        ? settlementKindMeta(props.settlementEntityType)?.entityKey
        : undefined;

    const Controls = (
        <>
            <Button
                appearance="primary"
                icon={isPrinting ? <Spinner size="extra-tiny" /> : <RiPrinterLine />}
                onClick={async () => {
                    if (isPrinting || !props.SettlementId || !props.accessToken) return;
                    setIsPrinting(true);
                    let pdfUrl: string | undefined;
                    try {
                        // Se descarga como archivo: abrirlo con window.open + 'noopener' devuelve null
                        // (daba error aunque la pestaña se abría) y el blob revocado no se podía guardar.
                        const pdfBlob = await GetSettlementPdf(props.SettlementId, props.accessToken);
                        pdfUrl = URL.createObjectURL(new Blob([pdfBlob], { type: 'application/pdf' }));
                        const link = document.createElement('a');
                        link.href = pdfUrl;
                        link.download = ComponentFunctions.settlementPdfFileName(props.Settlement?.name, props.SettlementId);
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                    } catch (error: any) {
                        dispatchToast(
                            <AppToast intent="error" title={t('settlementDetailsPage.printSettlementErrorTitle')} body={error?.ErrorMessage ?? t('settlementDetailsPage.printSettlementErrorBody')} />,
                            { timeout, position, intent: 'error' }
                        );
                    } finally {
                        if (pdfUrl) {
                            setTimeout(() => URL.revokeObjectURL(pdfUrl!), 1000);
                        }
                        setIsPrinting(false);
                    }
                }}
                disabled={isPrinting}
            >
                {t('settlementDetailsPage.printSettlementButton')}
            </Button>
            {props.onUploadInvoice && (
                <Tooltip
                    content={props.uploadInvoiceDisabledReason ?? t('azureFileUpload.buttons.uploadInvoiceTooltip')}
                    relationship="description"
                >
                    <Button
                        appearance="secondary"
                        icon={<ArrowUpload20Regular />}
                        onClick={props.onUploadInvoice}
                        disabledFocusable={!!props.uploadInvoiceDisabledReason}
                        data-testid="settlement-upload-invoice-button"
                    >
                        {t('azureFileUpload.buttons.uploadInvoice')}
                    </Button>
                </Tooltip>
            )}
            {props.Settlement?.status === SettlementStatusEnum.FINISHED && (
                <>
                    <SettlementChangeStatusConfirmationFormComponent SettlementId={props.SettlementId} NextStatusIsActionConfirmed={SettlementStatusEnum.CONFIRMED} />
                    <SettlementChangeStatusConfirmationFormComponent SettlementId={props.SettlementId} NextStatusIsActionConfirmed={SettlementStatusEnum.CANCELLED} />
                </>
            )}
            {props.Settlement?.status === SettlementStatusEnum.CONFIRMED && (
                <SettlementChangeStatusConfirmationFormComponent SettlementId={props.SettlementId} NextStatusIsActionConfirmed={SettlementStatusEnum.CANCELLED} />
            )}
        </>
    );

    return (
        <>
            <div className={`sh-root sh-root--${statusMod}`}>

                {/* ── Hero: status badge → name → description → period */}
                <div className="sh-hero">
                    <div className="sh-identity">
                        <span className={`sh-status-badge sh-status--${statusMod}`}>
                            <FontIcon
                                iconName={ComponentFunctions.GetSettlementStatusIcon(props.Settlement?.status)}
                                className={props.Settlement?.status === 2 ? 'withRotation' : ''}
                            />
                            &nbsp;{t(ComponentFunctions.GetSettlementStatusDescripcion(props.Settlement?.status))}
                        </span>
                        <div className="sh-name-row">
                            <h2 className="sh-name">{props.Settlement?.name ?? '—'}</h2>
                            {props.Settlement && (
                                <button type="button" className="sh-name-edit" title="Cambiar nombre" aria-label="Cambiar nombre" onClick={() => setIsRenaming(true)}>
                                    <FontIcon iconName="Edit" />
                                </button>
                            )}
                        </div>
                        {props.Settlement?.description && (
                            <p className="sh-description">{props.Settlement.description}</p>
                        )}
                        <div className="sh-period">
                            <FontIcon iconName="Calendar" className="sh-period-icon" />
                            <MOMENT format="DD/MM/YYYY">{props.Settlement?.settlementDateRangeInit}</MOMENT>
                            <span className="sh-period-sep">—</span>
                            <MOMENT format="DD/MM/YYYY">{props.Settlement?.settlementDateRangeEnd}</MOMENT>
                        </div>
                    </div>
                    <div className="sh-controls">
                        {props.Settlement?.status === SettlementStatusEnum.PAID && (
                            <span className="sh-watermark" aria-hidden>{t('SettlementStatus.paid')}</span>
                        )}
                        {Controls}
                    </div>
                </div>

                {/* ── Secondary meta strip */}
                <div className="sh-meta">
                    {settlementType && (
                        <div className="sh-meta-item">
                            <span className="sh-meta-label">Tipo</span>
                            <span className={`sh-meta-value sh-type-badge sh-type-badge--${settlementType.mod}`}>
                                <FontIcon iconName={settlementType.icon} className="sh-type-icon" />
                                {settlementType.label}
                            </span>
                        </div>
                    )}
                    {props.settlementEntityLabel && (
                        <div className="sh-meta-item">
                            <span className="sh-meta-label">
                                {entityLabelKey ? t(entityLabelKey) : 'Entidad'}
                            </span>
                            <span className={`sh-meta-value sh-entity-badge sh-entity-badge--${props.settlementEntityType}`}>
                                {props.settlementEntityLabel}
                            </span>
                        </div>
                    )}
                    <div className="sh-meta-item">
                        <span className="sh-meta-label">Identificador</span>
                        <span className="sh-meta-value sh-mono">{props.Settlement?.identifier ?? '—'}</span>
                    </div>
                    <div className="sh-meta-item">
                        <span className="sh-meta-label">Moneda</span>
                        <span className="sh-meta-value">
                            <span className="sh-currency-badge">{props.Settlement?.currencyCode ?? '—'}</span>
                        </span>
                    </div>
                    {props.Settlement?.status === 5 && props.Settlement?.voucheredNumber && (
                        <div className="sh-meta-item">
                            <span className="sh-meta-label">Factura</span>
                            <span className="sh-meta-value sh-mono">{props.Settlement.voucheredNumber}</span>
                        </div>
                    )}
                    {props.Settlement?.status === 5 && props.Settlement?.bussinessname && (
                        <div className="sh-meta-item">
                            <span className="sh-meta-label">Razón Social</span>
                            <span className="sh-meta-value">{props.Settlement.bussinessname}</span>
                        </div>
                    )}
                </div>

                {/* ── Deudas: desde que se confirma en adelante. "Deuda impaga" es el
                       estado al que vuelve una liquidación pagada cuando se desimputa el
                       cobro; la deuda sigue existiendo y es justo cuando hay que verla. */}
                {(props.Settlement?.status === SettlementStatusEnum.CONFIRMED ||
                  props.Settlement?.status === SettlementStatusEnum.PAID ||
                  props.Settlement?.status === SettlementStatusEnum.DEBT_UNPAID) && (
                    <div className="sh-debts">
                        <div
                            className={`sh-debts-toggle${debtsCollapsed ? ' sh-debts-toggle--collapsed' : ''}`}
                            onClick={() => setDebtsCollapsed(v => !v)}
                        >
                            <FontIcon iconName="CreditCardBill" className="sh-debts-toggle-icon" />
                            <span className="sh-debts-toggle-label">Deudas asociadas</span>
                            <FontIcon
                                iconName={debtsCollapsed ? 'ChevronDown' : 'ChevronUp'}
                                className="sh-debts-toggle-chevron"
                            />
                        </div>
                        {!debtsCollapsed && (
                            <div className="sh-debts-body">
                                <SettlementDebtsDetailsComponent SettlementId={props.SettlementId} />
                            </div>
                        )}
                    </div>
                )}
            </div>
            <SettlementNameFormComponent SettlementId={props.SettlementId} isOpen={isRenaming} onClose={() => setIsRenaming(false)} />
            <Toaster toasterId={'SettlementToast'} />
        </>
    );
};

export default connect(
    ComponentFunctions.mapStateToProps,
    ComponentFunctions.mapDispatchToProps
)(SettlementHeaderComponent as any);